<template>
    <button
        :class="badgeClass"
        :size="size"
        class="Badge"
        @click="clickable && $emit('onClick')"
    >
        <slot name="beforeText" />
        <div class="Wrapper">
            <span><slot /></span>
        </div>
        <slot name="afterText" />
        <WIcon
            v-if="close"
            icon="close"
            :size="16"
            @click="$emit('onClose')"
        />
    </button>
</template>

<script>
import VueTypes from 'vue-types'
import { capitalize } from 'lodash'

export default {
    name: 'Badge',
    props: {
        color: VueTypes.oneOf(['primary', 'secondary', 'tertiary', 'success', 'warning', 'info', 'error']).def('primary'),
        size: VueTypes.oneOf(['sm', '', 'lg']),
        width: VueTypes.oneOf(['sm', '', 'lg']),
        outline: VueTypes.bool.def(false),
        rounded: VueTypes.bool.def(false),
        shadow: VueTypes.bool.def(false),
        block: VueTypes.bool.def(false),
        uppercase: VueTypes.bool.def(false),
        close: VueTypes.bool.def(false),
        clickable: VueTypes.bool.def(false),
    },
    computed: {
        badgeClass () {
            return [
                this.color,
                this.size ? `size${capitalize(this.size)}` : '',
                this.width ? `width${capitalize(this.width)}` : '',
                {
                    outline: this.outline,
                    rounded: this.rounded,
                    shadow: this.shadow,
                    block: this.block,
                    uppercase: this.uppercase,
                    close: this.close,
                    clickable: this.clickable,
                },
            ]
        },
    },
}
</script>

<style scoped lang="scss">
$border-radius: 2rem;

.Wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover,
    &:focus,
    &:active {
        outline: none;
    }
}

.WIcon {
    position: absolute;
    top: -5px;
    right: -15px;
    cursor: pointer;
}

.Badge {
    position: relative;
    display: inline-flex;
    justify-content: center;
    margin: 5px;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    border: 1px solid transparent;
    background: transparent;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 16px;
    outline: none;
    transition: all .3s ease-in-out;
    cursor: default;

    &.primary {
        color: $white;
        border-color: $accent-blue;
        background: $accent-blue;
    }
    &.secondary {
        color: $primary;
        border-color: $white;
        background: $white;
    }
	&.tertiary {
		color: $white;
		border-color: $gray-01;
		background: $gray-01;
	}
    &.success {
        color: $white;
        border-color: $success;
        background: $success;
    }
    &.warning {
        color: $white;
        border-color: $warning;
        background: $warning;
    }
    &.error {
        color: $white;
        border-color: $danger;
        background: $danger;
    }
    &.info {
        color: $white;
        border-color: $info;
        background: $info;
    }

	&.outline {
		padding: 0.5rem 1rem;
        background-color: $white;
        border-radius: 2rem;
		border-width: 1px;
		border-style: solid;
        font-weight: $font-weight-normal;
        line-height: 1.4;

		&.primary {
			color: $accent-blue;
			border-color: $accent-blue;
		}
		&.secondary {
			color: $secondary;
			border-color: $secondary;
		}
		&.tertiary {
			color: $gray-01;
			border-color: $gray-01;
		}
		&.success {
			color: $success;
			border-color: $success;
		}
		&.warning {
			color: $warning;
			border-color: $warning;
		}
		&.info {
			color: $info;
			border-color: $info;
		}
		&.error {
			color: $danger;
			border-color: $danger;
		}
	}

    // Attr
    &.rounded {
        border-radius: $border-radius;
    }
    &.uppercase {
        text-transform: uppercase;
    }
    &.shadow {
        box-shadow: 0 3px 7px 0 rgba($gray-01, 0.2);
    }
    &.block {
        width: 100%;
    }
    &.clickable {
        cursor: pointer;
    }

    // Sizes
    &.widthSm {
        width: 120px;
    }
    &.widthLg {
        width: 220px;
    }
    &.sizeSm {
        padding-top: 0.22rem;
        padding-bottom: 0.22rem;
        font-size: 12px;
    }
    &.sizeLg {
        padding: 1rem 2rem;
        font-size: 16px;
        font-weight: 800;
        line-height: 1.38;
    }
}
</style>
